import axios, { AxiosError } from "axios";
import _isEmpty from "lodash/isEmpty";
import React from "react";
import {
  AccountType,
  getErrorPopupTexts,
  ICheckboxGroupValue,
  isCheckboxGroupValueValid,
} from "component-library";

import { IPaginatedData } from "shared/models/common";
import ExpiringUserStorageService from "shared/services/api/localStorage/ExpiringUserStorageService";
import localStorageService from "shared/services/api/localStorage/localStorageService";
import scrollManager from "shared/services/scrollManager";
import { Diff, ValueOf } from "shared/types/utilities";

import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";

import { IWithRequestAbortController } from "types/axios";

import {
  ISelfTapeRequestNotification,
  ISelfTapeRequestNotificationFilterParams,
  ISelfTapeRequestNotificationFilters,
  ISelfTapeRequestNotificationListPaginated,
  ISelfTapeRequestNotificationStatus,
} from "models/selfTapeRequest";

import getErrorPopupType from "helpers/getErrorPopupType";
import {
  mapCheckboxGroupValueToNumericValues,
  mapNumericOptionsToCheckboxGroupValue,
} from "helpers/mappers";

import { IJobsPageInjectedProps } from "pages/JobsPage/JobsPage";
import { JobsPageTab } from "pages/JobsPage/JobsPage.config";

import SelfTapeRequestNotificationFilterControl from "components/SelfTapeRequestNotificationFilterControl/SelfTapeRequestNotificationFilterControl";
import {
  ISelfTapeRequestNotificationTableItem,
  SelfTapeRequestNotificationUuid,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.models";

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_VALUES,
  FilterControlType,
  FilterType,
  IFilterConfig,
  IFilterModel,
} from "./withSelfTapeRequestNotificationsFilter.config";
import { FilterModelToSelfTapeRequestNotificationFilterParamsConverter } from "./withSelfTapeRequestNotificationsFilter.converter";
import { prepareFilterConfig } from "./withSelfTapeRequestNotificationsFilter.helper";
import { RouterProps } from "hoc/withRouter";

export type ChangeFilters = (
  values: Partial<IFilterModel>,
  callback?: (updatedModel: IFilterModel) => void
) => void;

export type AddFilterValue = (
  filterType: FilterType,
  value: ValueOf<IFilterModel>
) => void;

export interface IWithSelfTapeRequestNotificationsFilterTexts {
  clientDropdownPlaceholder: string;
  clientSearchPlaceholder: string;
  dateReceivedInputPlaceholder: string;
}

export interface IWithSelfTapeRequestNotificationsFilterInjectedProps {
  filterConfig: IFilterConfig;
  changeFilters: ChangeFilters;
  addFilterValue: AddFilterValue;
  filterValues: IFilterModel;
  renderSelfTapeRequestNotificationsFilterControl(): React.ReactNode;
  isLoading: boolean;
  paginatedItems: IPaginatedData<ISelfTapeRequestNotificationTableItem>;
  onMarkAsRead(itemIds: SelfTapeRequestNotificationUuid[]): Promise<void>;
  onMarkAsUnread(itemIds: SelfTapeRequestNotificationUuid[]): Promise<void>;
}

export interface IWithSelfTapeRequestNotificationsFilterProps
  extends IWithSelfTapeRequestNotificationsFilterInjectedProps,
    RouterProps,
    IJobsPageInjectedProps {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  convertToTableItems: (
    item: ISelfTapeRequestNotification[]
  ) => ISelfTapeRequestNotificationTableItem[];
  getSelfTapeRequestNotificationFilters(
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationFilters>;
  findSelfTapeRequestNotifications(
    params: ISelfTapeRequestNotificationFilterParams,
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationListPaginated>;
  userType: AccountType;
  userId: string;
  filterControlTexts: IWithSelfTapeRequestNotificationsFilterTexts;
  updateStatus(
    statuses: ISelfTapeRequestNotificationStatus[],
    abortController?: AbortController
  ): Promise<void>;
}

export interface IWithSelfTapeRequestNotificationsFilterState {
  filterConfig: IFilterConfig;
  values: IFilterModel;
  isLoading: boolean;
  selfTapeRequestNotifications: ISelfTapeRequestNotificationListPaginated;
  filters?: ISelfTapeRequestNotificationFilters;
}

export interface IWithSelfTapeRequestNotificationsFilterOptions {
  tab: JobsPageTab;
}

type HocProps<TProps extends object> = Diff<
  TProps,
  IWithSelfTapeRequestNotificationsFilterInjectedProps
>;

export default function withSelfTapeRequestNotificationsFilterFactory<
  TProps extends IWithSelfTapeRequestNotificationsFilterProps,
>(options: IWithSelfTapeRequestNotificationsFilterOptions) {
  return (
    WrappedComponent: React.ComponentType<React.PropsWithChildren<TProps>>
  ): React.ComponentType<React.PropsWithChildren<HocProps<TProps>>> => {
    class WithSelfTapeRequestNotificationsFilter
      extends React.Component<
        HocProps<TProps>,
        IWithSelfTapeRequestNotificationsFilterState
      >
      implements IWithRequestAbortController
    {
      public abortController = new AbortController();

      private storageKey = `filter:${options.tab}`;
      private storageService = new ExpiringUserStorageService({
        storage: localStorageService,
        ttl: 1000 * 60 * 60 * 12,
        userId: this.props.userId,
      });
      private converter =
        new FilterModelToSelfTapeRequestNotificationFilterParamsConverter();

      constructor(props: HocProps<TProps>) {
        super(props);

        const filterConfig = this.setupFilterConfig();

        this.state = {
          filterConfig,
          values: this.generateDefaultValues(filterConfig, props.page),
          isLoading: false,
          selfTapeRequestNotifications: {
            count: 0,
            items: [],
          },
        };
      }

      public componentDidMount() {
        this.loadSelfTapeRequestNotifications(true);
        this.loadSelfTapeRequestNotificationFilters();
      }

      public componentDidUpdate(
        prevProps: React.PropsWithChildren<HocProps<TProps>>,
        prevState: IWithSelfTapeRequestNotificationsFilterState
      ) {
        const propsPageNumber = this.props.page;
        const statePageNumber = this.state.values[FilterType.PageNumber];

        if (
          this.props.pageTranslationLocale !== prevProps.pageTranslationLocale
        ) {
          this.setState({
            filterConfig: this.setupFilterConfig(prevState.filters),
          });
        }

        if (
          propsPageNumber !== prevProps.page &&
          propsPageNumber !== statePageNumber
        ) {
          this.changeFilters({ [FilterType.PageNumber]: propsPageNumber });
        } else if (propsPageNumber !== statePageNumber) {
          const [, setSearchParams] = prevProps.router.searchParams;

          setSearchParams();
        }
      }

      public componentWillUnmount() {
        this.abortController.abort?.();
      }

      public render() {
        const {
          filterConfig,
          values,
          isLoading,
          selfTapeRequestNotifications: { count = 0, items = [] },
        } = this.state;

        const { convertToTableItems } = this.props;

        return (
          <WrappedComponent
            {...(this.props as TProps)}
            filterConfig={filterConfig}
            changeFilters={this.changeFilters}
            addFilterValue={this.addFilterValue}
            filterValues={values}
            paginatedItems={{
              count,
              items: convertToTableItems(items),
            }}
            isLoading={isLoading}
            renderSelfTapeRequestNotificationsFilterControl={this.renderControl}
            onMarkAsRead={this.handleMarkAsRead}
            onMarkAsUnread={this.handleMarkAsUnread}
          />
        );
      }

      public renderControl = () => {
        const { locale, userType, filterControlTexts } = this.props;
        const { filterConfig, values } = this.state;
        const placeholders = {
          clientDropdownPlaceholder:
            filterControlTexts.clientDropdownPlaceholder,
          clientSearchPlaceholder: filterControlTexts.clientSearchPlaceholder,
          dateReceivedInputPlaceholder:
            filterControlTexts.dateReceivedInputPlaceholder,
        };

        return (
          <SelfTapeRequestNotificationFilterControl
            filterConfig={filterConfig}
            accountType={userType}
            filterValues={values}
            locale={locale}
            placeholders={placeholders}
            changeFilters={this.changeFilters}
          />
        );
      };

      private changeFilters: ChangeFilters = (values, callback) => {
        const updatedValues = {
          ...this.state.values,
          [FilterType.PageNumber]: DEFAULT_PAGE_NUMBER,
          ...values,
        };

        this.setState({ values: updatedValues }, () => {
          callback?.(updatedValues);
          void this.loadSelfTapeRequestNotifications();
          this.preserveFilters(updatedValues);
        });
      };

      private addFilterValue: AddFilterValue = (filterType, value) => {
        const { values, filterConfig } = this.state;
        const { drawerFilterControlType, checkboxOptions } =
          filterConfig[filterType];
        let updatedValue;

        if (
          drawerFilterControlType === FilterControlType.Checkbox &&
          value !== undefined
        ) {
          updatedValue = mapNumericOptionsToCheckboxGroupValue(
            checkboxOptions,
            [
              ...mapCheckboxGroupValueToNumericValues(
                values[filterType] as ICheckboxGroupValue
              ),
              value as number,
            ]
          );
        }

        this.changeFilters({ [filterType]: updatedValue });
      };

      private loadSelfTapeRequestNotificationFilters = async () => {
        const { getSelfTapeRequestNotificationFilters } = this.props;

        const filters = await getSelfTapeRequestNotificationFilters(
          this.abortController
        );
        const filterConfig = this.setupFilterConfig(filters);

        this.setState({ filterConfig, filters });
      };

      private async loadSelfTapeRequestNotifications(isInitial?: boolean) {
        const { restoreScrollPosition, findSelfTapeRequestNotifications } =
          this.props;
        const { values } = this.state;

        this.setState({ isLoading: true });
        this.abortController.abort?.();
        this.abortController = new AbortController();

        try {
          const selfTapeRequestNotifications =
            await findSelfTapeRequestNotifications(
              this.converter.convert(values),
              this.abortController
            );

          this.setState({ selfTapeRequestNotifications }, () => {
            if (isInitial && restoreScrollPosition) {
              scrollManager.scrollTo(restoreScrollPosition, 0);
            }
          });
        } catch (e) {
          this.handleError(e);
        } finally {
          this.setState({ isLoading: false });
        }
      }

      private handleMarkAsRead = (
        itemIds: SelfTapeRequestNotificationUuid[]
      ) => {
        this.toggleReadStatus(itemIds, true);
      };

      private handleMarkAsUnread = (
        itemIds: SelfTapeRequestNotificationUuid[]
      ) => {
        this.toggleReadStatus(itemIds, false);
      };

      private async toggleReadStatus(
        itemIds: SelfTapeRequestNotificationUuid[],
        acknowledged: boolean
      ) {
        const statuses: ISelfTapeRequestNotificationStatus[] = itemIds.map(
          (id) => ({ id, acknowledged })
        );
        this.setState({ isLoading: true });

        try {
          await this.props.updateStatus(statuses);
          this.changeFilters({}, this.props.refreshCounters);
        } catch (e) {
          this.handleError(e);
        }
      }

      private handleError(e: AxiosError) {
        if (axios.isCancel(e)) {
          return;
        }

        this.props.showPopup({
          type: PopupType.ErrorPopup,
          data: {
            ...getErrorPopupTexts()[getErrorPopupType()],
            hidePopup: this.props.hidePopup,
          },
        });
      }

      private setupFilterConfig = (
        filters?: ISelfTapeRequestNotificationFilters
      ): IFilterConfig => {
        const { performers = [] } = filters || {};

        return prepareFilterConfig({
          filterControlTexts: this.props.filterControlTexts,
          userType: this.props.userType,
          performers,
        });
      };

      private preserveFilters(values: IFilterModel) {
        this.storageService.set<IFilterModel>(this.storageKey, values);
      }

      private generateDefaultValues(
        filterConfig: IFilterConfig,
        page: number
      ): IFilterModel {
        const values = this.storageService.get<IFilterModel>(this.storageKey);
        const updatedDefaultValues = {
          ...DEFAULT_VALUES,
        };

        if (!values) {
          return { ...updatedDefaultValues, [FilterType.PageNumber]: page };
        }

        const clientTypesValue = values[FilterType.Client];
        const clientFilter =
          !_isEmpty(clientTypesValue) &&
          isCheckboxGroupValueValid(
            {
              name: FilterType.Client,
              options: filterConfig[FilterType.Client]!.checkboxOptions!,
            },
            clientTypesValue
          )
            ? clientTypesValue
            : updatedDefaultValues[FilterType.Client];

        const dateReceivedValue = values[FilterType.DateReceived] as
          | string[]
          | undefined;
        const dateReceivedFilter =
          dateReceivedValue &&
          Date.parse(dateReceivedValue[0]) &&
          Date.parse(dateReceivedValue[1])
            ? [new Date(dateReceivedValue[0]), new Date(dateReceivedValue[1])]
            : updatedDefaultValues[FilterType.DateReceived];

        return {
          ...updatedDefaultValues,
          ...values,
          [FilterType.Client]: clientFilter,
          [FilterType.MediaRequestType]:
            filterConfig[FilterType.MediaRequestType].defaultValue,
          [FilterType.DateReceived]: dateReceivedFilter,
          [FilterType.PageNumber]: page,
        };
      }
    }

    return WithSelfTapeRequestNotificationsFilter;
  };
}
